import React, { useEffect, useState } from "react";
import { Button, Card, Row, Col, Modal, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { FaTrash, FaEdit, FaStar } from "react-icons/fa";
import api from "../services/api";
import AddressSearch from "../components/AddressSearch";
import { isValidKoreanMobile, formatKoreanMobile } from "../services/phone";

const EMPTY_ADDRESS = {
  full_name: "",
  address_type: "shipping", // billing removed
  postal_code: "",
  road_address: "",
  address_line: "",
  street: "",
  address_details: "",
  sido: "",
  sigungu: "",
  bname: "",
  building_name: "",
  address_english: "",
  address_source: "manual",
  country: "South Korea",
  phone: "",
};

const MyAddress = () => {
  const { t } = useTranslation();
  const [addresses, setAddresses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(EMPTY_ADDRESS);
  const [addrErrors, setAddrErrors] = useState({});
  const [editingAddressId, setEditingAddressId] = useState(null);

  const fetchAddresses = async () => {
    try {
      const response = await api.get("/addresses");
      setAddresses(response.data || []);
    } catch (error) {
      toast.error(t("checkout.addressLoadFailed"));
    }
  };

  useEffect(() => {
    fetchAddresses();
  }, []);

  const resetForm = () => {
    setFormData(EMPTY_ADDRESS);
    setAddrErrors({});
  };

  const handleShowModal = () => {
    setEditMode(false);
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (address) => {
    setEditMode(true);
    setEditingAddressId(address._id);
    setFormData({ ...EMPTY_ADDRESS, ...address });
    setAddrErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/addresses/${id}`);
      toast.success(t("addresses.deleted"));
      fetchAddresses();
    } catch {
      toast.error(t("addresses.deleteFailed"));
    }
  };

  const handleDefault = async (address) => {
    const isAlreadyDefault = address.is_default_shipping;

    if (isAlreadyDefault) {
      toast.info(t("addresses.alreadyDefault"));
      return;
    }

    try {
      for (const addr of addresses) {
        if (addr._id !== address._id && addr.is_default_shipping) {
          await api.patch(`/addresses/${addr._id}`, { is_default_shipping: false });
        }
      }

      await api.patch(`/addresses/${address._id}`, { is_default_shipping: true });
      toast.success(t("addresses.defaultSet"));
      fetchAddresses();
    } catch (error) {
      toast.error(t("addresses.defaultFailed"));
    }
  };

  // The address itself can only come from the postcode search, so the only
  // things worth checking here are "did they search" and "did they add a unit".
  const validateAddress = () => {
    const errors = {};
    if (!formData.postal_code || !(formData.road_address || formData.address_line)) {
      errors.address = t("address.required");
    }
    if (!formData.address_details?.trim()) {
      errors.detail = t("address.detailRequired");
    }
    setAddrErrors(errors);
    if (!isValidKoreanMobile(formData.phone)) {
      toast.error(t("auth.invalidPhone")); // the courier calls this number
      return false;
    }
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateAddress()) return;

    // Prevent duplicate addresses
    const duplicate = addresses.some((addr) =>
      addr._id !== editingAddressId &&
      addr.full_name === formData.full_name &&
      addr.postal_code === formData.postal_code &&
      (addr.road_address || addr.address_line) === (formData.road_address || formData.address_line) &&
      (addr.address_details || "") === (formData.address_details || "") &&
      addr.phone === formData.phone
    );

    if (duplicate && !editMode) {
      toast.warning(t("addresses.duplicate"));
      return;
    }

    try {
      if (editMode) {
        await api.put(`/addresses/${editingAddressId}`, formData);
        toast.success(t("addresses.updated"));
      } else {
        await api.post("/addresses", formData);
        toast.success(t("checkout.addressAdded"));
      }

      setShowModal(false);
      fetchAddresses();
    } catch (err) {
      // The server re-validates the postcode — show its reason, not a generic one.
      toast.error(err?.response?.data?.message || t("checkout.addressSaveFailed"));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "phone" ? formatKoreanMobile(value) : value,
    }));
  };

  // Patch coming back from the postcode search (or the detail input).
  const handleAddressChange = (patch) => {
    setFormData((prev) => ({ ...prev, ...patch }));
    setAddrErrors({});
  };

  const isDefaultAddress = (address) => address.is_default_shipping;

  return (
    <div className="container my-4">
      <h3 className="text-center mb-4">{t("nav.myAddresses")}</h3>
      <div className="text-end mb-3">
        <Button variant="warning" onClick={handleShowModal}>
          + {t("addresses.add")}
        </Button>
      </div>

      {addresses.length === 0 ? (
        <div className="text-center py-5">
          <p className="text-muted">{t("addresses.none")}</p>
        </div>
      ) : (
        <Row>
          {addresses.map((address) => (
            <Col md={12} key={address._id} className="mb-4">
              <Card className="shadow-sm position-relative">
                {/* Delete button */}
                <Button
                  variant="link"
                  className="position-absolute top-0 end-0 m-2 text-danger"
                  onClick={() => handleDelete(address._id)}
                  title="Delete Address"
                >
                  <FaTrash />
                </Button>

                <Card.Body>
                  <Card.Title className="d-flex align-items-center gap-2">
                    {address.full_name}
                    {isDefaultAddress(address) && (
                      <FaStar className="text-warning" title="Default" />
                    )}
                  </Card.Title>

                  <Card.Text>
                    <strong>{t("address.postcode")}:</strong> {address.postal_code} <br />
                    {address.road_address || address.address_line || address.street}
                    {address.address_details ? ` ${address.address_details}` : ""} <br />
                    <strong>{t("checkout.phone")}:</strong> {address.phone}
                  </Card.Text>

                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleEdit(address)}
                    >
                      <FaEdit className="me-1" />
                      {t("addresses.edit")}
                    </Button>
                    <Button
                      variant={isDefaultAddress(address) ? "success" : "outline-warning"}
                      size="sm"
                      onClick={() => handleDefault(address)}
                      disabled={isDefaultAddress(address)}
                    >
                      {isDefaultAddress(address) ? t("checkout.default") : t("addresses.setDefault")}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Address Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editMode ? t("addresses.editTitle") : t("addresses.add")}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-2">
              <Form.Label>{t("checkout.fullName")}</Form.Label>
              <Form.Control
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                required
              />
            </Form.Group>

            {/* Commented Billing Option */}
            {/* <Form.Group className="mb-2">
              <Form.Label>{t("addresses.type")}</Form.Label>
              <Form.Select
                name="address_type"
                value={formData.address_type}
                onChange={handleChange}
              >
                <option value="shipping">{t("addresses.shipping")}</option>
                <option value="billing">{t("addresses.billing")}</option>
              </Form.Select>
            </Form.Group> */}

            <Form.Group className="mb-3">
              <AddressSearch
                value={formData}
                onChange={handleAddressChange}
                error={addrErrors.address}
                detailError={addrErrors.detail}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>{t("checkout.phone")}</Form.Label>
              <Form.Control
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              {t("orders.cancel")}
            </Button>
            <Button type="submit" variant="warning">
              {editMode ? t("addresses.update") : t("addresses.addBtn")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default MyAddress;
